/**
 * Notifications settings — where a merchant hears that money landed.
 *
 * Two human channels, each with its own per-event mask:
 *   • Telegram  — the bot DMs you (master switch = the existing setting).
 *   • Discord   — posts to a channel via an incoming-webhook URL.
 *
 * The signed HTTP webhook (Developer mode) is the machine contract and is
 * unaffected by anything here. Each control persists immediately (optimistic,
 * with rollback on error) so there's no "did it save?" ambiguity.
 */

import { useEffect, useState } from "react";
import { Banner, Btn, Toggle } from "./ui";
import { useT } from "../lib/i18n";
import {
  ApiError,
  createTelegramLink,
  disconnectTelegram,
  getMyMerchant,
  getNotificationPrefs,
  setNotificationsEnabled,
  testDiscordNotification,
  updateNotificationPrefs,
  type NotificationPrefs,
  type NotifyEvent,
} from "../lib/api";

const EVENT_LABEL_KEYS: Record<NotifyEvent, string> = {
  paid: "notif.event_paid",
  failed: "notif.event_failed",
  expired: "notif.event_expired",
};

// Light client-side shape check; the server does the authoritative validation.
const looksLikeDiscordWebhook = (u: string): boolean =>
  /^https:\/\/(?:[a-z]+\.)?discord(?:app)?\.com\/api\/(?:v\d+\/)?webhooks\/\d+\/\S+$/.test(u.trim());

function EventChips({
  events,
  selected,
  disabled,
  onToggle,
}: {
  events: NotifyEvent[];
  selected: NotifyEvent[];
  disabled?: boolean;
  onToggle: (next: NotifyEvent[]) => void;
}) {
  const t = useT();
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {events.map((e) => {
        const on = selected.includes(e);
        return (
          <button
            key={e}
            type="button"
            disabled={disabled}
            aria-pressed={on}
            onClick={() => onToggle(on ? selected.filter((x) => x !== e) : [...selected, e])}
            style={{
              padding: "6px 13px",
              borderRadius: "var(--r-pill)",
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "inherit",
              cursor: disabled ? "default" : "pointer",
              border: `1px solid ${on ? "var(--brand)" : "var(--line-2)"}`,
              background: on ? "var(--brand-50)" : "var(--surface)",
              color: on ? "var(--brand-700)" : "var(--ink-3)",
              opacity: disabled ? 0.5 : 1,
              transition: "all .12s ease",
            }}
          >
            {t(EVENT_LABEL_KEYS[e])}
          </button>
        );
      })}
    </div>
  );
}

export function NotificationsCard() {
  const t = useT();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Discord URL is edited locally; persisted on "Save".
  const [discordUrl, setDiscordUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Telegram connect state — a web/wallet merchant must link a real chat.
  const [tgConnected, setTgConnected] = useState<boolean | null>(null);
  const [tgWaiting, setTgWaiting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, m] = await Promise.all([getNotificationPrefs(), getMyMerchant()]);
        if (cancelled) return;
        setPrefs(p);
        setDiscordUrl(p.discord_webhook_url ?? "");
        setTgConnected(m.telegram_connected === true);
      } catch (e) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Open the connect deep-link, then poll the profile until the bot captures
  // the chat (the merchant taps Start in Telegram). Stops after ~90s.
  const connectTelegram = async () => {
    setErr(null);
    try {
      const { url } = await createTelegramLink();
      window.open(url, "_blank", "noopener,noreferrer");
      setTgWaiting(true);
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const m = await getMyMerchant();
          if (m.telegram_connected) {
            setTgConnected(true);
            setTgWaiting(false);
            flashSaved();
            return;
          }
        } catch {
          /* keep polling */
        }
      }
      setTgWaiting(false);
    } catch (e) {
      setTgWaiting(false);
      fail(e);
    }
  };

  const disconnectTg = async () => {
    setErr(null);
    try {
      await disconnectTelegram();
      setTgConnected(false);
      flashSaved();
    } catch (e) {
      fail(e);
    }
  };

  const flashSaved = () => {
    setErr(null);
    setSavedAt(Date.now());
  };
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : String(e));

  // Optimistically patch local prefs, persist, roll back on failure.
  const patch = async (next: Partial<NotificationPrefs>, persist: () => Promise<unknown>) => {
    if (!prefs) return;
    const prev = prefs;
    setPrefs({ ...prefs, ...next });
    try {
      await persist();
      flashSaved();
    } catch (e) {
      setPrefs(prev);
      fail(e);
    }
  };

  const toggleTelegramMaster = (on: boolean) =>
    patch({ telegram_dm_enabled: on }, () => setNotificationsEnabled(on));

  const setTelegramEvents = (events: NotifyEvent[]) =>
    patch({ telegram_events: events }, () => updateNotificationPrefs({ telegram_events: events }));

  const setDiscordEvents = (events: NotifyEvent[]) =>
    patch({ discord_events: events }, () => updateNotificationPrefs({ discord_events: events }));

  const toggleDiscordEnabled = (on: boolean) =>
    patch({ discord_enabled: on }, () => updateNotificationPrefs({ discord_enabled: on }));

  const saveDiscordUrl = async () => {
    const url = discordUrl.trim();
    setSavingUrl(true);
    setTestMsg(null);
    try {
      const r = await updateNotificationPrefs({ discord_webhook_url: url || null });
      setPrefs((p) =>
        p
          ? {
              ...p,
              discord_webhook_url: r.discord_webhook_url,
              discord_enabled: r.discord_enabled,
            }
          : p,
      );
      flashSaved();
    } catch (e) {
      fail(e);
    } finally {
      setSavingUrl(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestMsg(null);
    try {
      await testDiscordNotification(discordUrl.trim() || undefined);
      setTestMsg({ ok: true, text: t("notif.test_sent") });
    } catch (e) {
      setTestMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setTesting(false);
    }
  };

  if (!prefs) {
    return (
      <>
        <div className="s-label">{t("notif.title")}</div>
        <div className="card card-pad">
          <div className="help">{t("notif.loading")}</div>
        </div>
      </>
    );
  }

  const urlDirty = discordUrl.trim() !== (prefs.discord_webhook_url ?? "");
  const urlValid = discordUrl.trim() === "" || looksLikeDiscordWebhook(discordUrl);
  const hasSavedUrl = !!prefs.discord_webhook_url;

  return (
    <>
      <div className="s-label">{t("notif.title")}</div>

      {err && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="fail">{err}</Banner>
        </div>
      )}
      {savedAt && !err && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="ok">{t("notif.saved_at")} {new Date(savedAt).toLocaleTimeString()}</Banner>
        </div>
      )}

      {/* Telegram */}
      <div className="card card-pad">
        <div className="kv" style={{ borderBottom: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: "var(--ink)" }}>{t("notif.telegram_dm")}</div>
            <div className="help">
              {t("notif.telegram_help")}
            </div>
          </div>
          <Toggle
            on={prefs.telegram_dm_enabled}
            onChange={(n) => void toggleTelegramMaster(n)}
            ariaLabel={t("notif.telegram_toggle_aria")}
          />
        </div>
        {prefs.telegram_dm_enabled && (
          <>
            {/* Connect status — DMs only deliver once a real chat is linked. */}
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: 10,
                background: tgConnected ? "var(--ok-bg, #effaf2)" : "var(--warn-bg, #fff8e6)",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: 180, fontSize: 13 }}>
                {tgConnected === null ? (
                  <span style={{ color: "var(--ink-3)" }}>{t("notif.tg_checking")}</span>
                ) : tgConnected ? (
                  <span style={{ color: "var(--ink)" }}>
                    ✓ <b>{t("notif.tg_connected_bold")}</b> {t("notif.tg_connected_rest")}
                  </span>
                ) : (
                  <span style={{ color: "var(--ink)" }}>
                    <b>{t("notif.tg_not_connected_bold")}</b> {t("notif.tg_not_connected_pre")} <b>{t("notif.tg_start")}</b> {t("notif.tg_not_connected_post")}
                  </span>
                )}
              </div>
              {tgConnected ? (
                <Btn variant="ghost" onClick={() => void disconnectTg()}>
                  {t("notif.disconnect")}
                </Btn>
              ) : (
                <Btn onClick={() => void connectTelegram()} loading={tgWaiting} disabled={tgWaiting}>
                  {tgWaiting ? t("notif.waiting_telegram") : t("notif.connect_telegram")}
                </Btn>
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              <div className="help" style={{ marginBottom: 8 }}>
                {t("notif.notify_me_about")}
              </div>
              <EventChips
                events={prefs.events}
                selected={prefs.telegram_events}
                onToggle={(ev) => void setTelegramEvents(ev)}
              />
            </div>
          </>
        )}
      </div>

      {/* Discord */}
      <div className="card card-pad" style={{ marginTop: 12 }}>
        <div className="kv" style={{ borderBottom: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: "var(--ink)" }}>Discord</div>
            <div className="help">
              {t("notif.discord_help")}
            </div>
          </div>
          <Toggle
            on={prefs.discord_enabled}
            onChange={(n) => void toggleDiscordEnabled(n)}
            ariaLabel={t("notif.discord_toggle_aria")}
          />
        </div>

        <div className="field" style={{ marginTop: 14 }}>
          <label htmlFor="discord-url">{t("notif.webhook_url_label")}</label>
          <input
            id="discord-url"
            className="input"
            type="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://discord.com/api/webhooks/…"
            value={discordUrl}
            onChange={(e) => setDiscordUrl(e.target.value)}
          />
          <div className="help">
            {t("notif.discord_url_help")}
          </div>
        </div>

        {!urlValid && (
          <div style={{ marginTop: 8 }}>
            <Banner tone="warn">{t("notif.invalid_webhook")}</Banner>
          </div>
        )}
        {testMsg && (
          <div style={{ marginTop: 8 }}>
            <Banner tone={testMsg.ok ? "ok" : "fail"}>{testMsg.text}</Banner>
          </div>
        )}

        <div className="row" style={{ marginTop: 12, gap: 8, display: "flex", flexWrap: "wrap" }}>
          <Btn
            onClick={() => void saveDiscordUrl()}
            disabled={savingUrl || !urlDirty || !urlValid}
            loading={savingUrl}
          >
            {t("notif.save_webhook")}
          </Btn>
          <Btn
            variant="secondary"
            onClick={() => void sendTest()}
            disabled={testing || (!discordUrl.trim() && !hasSavedUrl) || !urlValid}
            loading={testing}
          >
            {t("notif.send_test")}
          </Btn>
        </div>

        {prefs.discord_enabled && hasSavedUrl && (
          <div style={{ marginTop: 14 }}>
            <div className="help" style={{ marginBottom: 8 }}>
              {t("notif.notify_channel_about")}
            </div>
            <EventChips
              events={prefs.events}
              selected={prefs.discord_events}
              onToggle={(ev) => void setDiscordEvents(ev)}
            />
          </div>
        )}
      </div>
    </>
  );
}
